import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FieldHeatMap = () => {
  const [selectedMetric, setSelectedMetric] = useState('effectiveness');
  const [selectedField, setSelectedField] = useState(null);

  const fields = [
    {
      id: 'field-a1',
      name: 'North Field A1',
      effectiveness: 94,
      cost: 1250,
      applications: 8,
      acres: 25.5,
      cropType: 'Tomatoes',
      lastTreatment: '2024-11-14',
      pestPressure: 'Low',
      coordinates: { x: 10, y: 10, width: 80, height: 60 }
    },
    {
      id: 'field-b2',
      name: 'East Field B2',
      effectiveness: 87,
      cost: 980,
      applications: 6,
      acres: 18.2,
      cropType: 'Peppers',
      lastTreatment: '2024-11-12',
      pestPressure: 'Medium',
      coordinates: { x: 100, y: 10, width: 70, height: 50 }
    },
    {
      id: 'field-c3',
      name: 'South Field C3',
      effectiveness: 76,
      cost: 1580,
      applications: 12,
      acres: 32.8,
      cropType: 'Cucumbers',
      lastTreatment: '2024-11-15',
      pestPressure: 'High',
      coordinates: { x: 10, y: 80, width: 90, height: 70 }
    },
    {
      id: 'field-d4',
      name: 'West Field D4',
      effectiveness: 91,
      cost: 750,
      applications: 5,
      acres: 15.6,
      cropType: 'Lettuce',
      lastTreatment: '2024-11-13',
      pestPressure: 'Low',
      coordinates: { x: 110, y: 70, width: 60, height: 80 }
    },
    {
      id: 'field-e5',
      name: 'Central Field E5',
      effectiveness: 82,
      cost: 1120,
      applications: 9,
      acres: 22.4,
      cropType: 'Herbs',
      lastTreatment: '2024-11-16',
      pestPressure: 'Medium',
      coordinates: { x: 180, y: 20, width: 75, height: 65 }
    }
  ];

  const metricOptions = [
    { id: 'effectiveness', label: 'Effectiveness', icon: 'Target' },
    { id: 'cost', label: 'Cost per Acre', icon: 'DollarSign' },
    { id: 'applications', label: 'Applications', icon: 'Droplets' },
    { id: 'pestPressure', label: 'Pest Pressure', icon: 'Bug' }
  ];

  const getFieldColor = (field) => {
    switch (selectedMetric) {
      case 'effectiveness':
        if (field?.effectiveness >= 90) return '#2D5A27'; // High effectiveness - dark green
        if (field?.effectiveness >= 80) return '#F4A460'; // Medium effectiveness - harvest gold
        return '#DC2626'; // Low effectiveness - red
      case 'cost':
        const costPerAcre = field?.cost / field?.acres;
        if (costPerAcre <= 40) return '#2D5A27'; // Low cost - green
        if (costPerAcre <= 60) return '#F4A460'; // Medium cost - gold
        return '#DC2626'; // High cost - red
      case 'applications':
        if (field?.applications <= 6) return '#2D5A27'; // Few applications - green
        if (field?.applications <= 9) return '#F4A460'; // Medium applications - gold
        return '#DC2626'; // Many applications - red
      case 'pestPressure':
        if (field?.pestPressure === 'Low') return '#2D5A27';
        if (field?.pestPressure === 'Medium') return '#F4A460';
        return '#DC2626';
      default:
        return '#6B7280';
    }
  };

  const getMetricValue = (field) => {
    switch (selectedMetric) {
      case 'effectiveness':
        return `${field?.effectiveness}%`;
      case 'cost':
        return `$${(field?.cost / field?.acres)?.toFixed(2)}`;
      case 'applications':
        return field?.applications;
      case 'pestPressure':
        return field?.pestPressure;
      default:
        return '';
    }
  };

  const handleFieldClick = (field) => {
    setSelectedField(selectedField?.id === field?.id ? null : field);
  };

  return (
    <div className="bg-card rounded-lg border border-border agricultural-shadow">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div className="mb-4 lg:mb-0">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Field Performance Heat Map
            </h2>
            <p className="text-sm text-muted-foreground">
              Visual analysis of treatment performance across all field zones
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {metricOptions?.map((option) => (
              <Button
                key={option?.id}
                variant={selectedMetric === option?.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedMetric(option?.id)}
                iconName={option?.icon}
                iconPosition="left"
              >
                {option?.label}
              </Button>
            ))}
          </div>
        </div>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Heat Map Visualization */}
          <div className="xl:col-span-2">
            <div className="relative bg-muted/30 rounded-lg p-4 h-96 overflow-hidden">
              <svg
                viewBox="0 0 280 180"
                className="w-full h-full"
                style={{ maxHeight: '100%' }}
              >
                {/* Background Grid */}
                <defs>
                  <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                    <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#E5E7EB" strokeWidth="0.5"/>
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />
                
                {/* Field Zones */}
                {fields?.map((field) => (
                  <g key={field?.id}>
                    <rect
                      x={field?.coordinates?.x}
                      y={field?.coordinates?.y}
                      width={field?.coordinates?.width}
                      height={field?.coordinates?.height}
                      fill={getFieldColor(field)}
                      stroke={selectedField?.id === field?.id ? '#1F2937' : 'transparent'}
                      strokeWidth={selectedField?.id === field?.id ? 2 : 0}
                      opacity={0.8}
                      className="cursor-pointer hover:opacity-100 smooth-transition"
                      onClick={() => handleFieldClick(field)}
                    />
                    <text
                      x={field?.coordinates?.x + field?.coordinates?.width / 2}
                      y={field?.coordinates?.y + field?.coordinates?.height / 2 - 8}
                      textAnchor="middle"
                      className="fill-white text-xs font-medium pointer-events-none"
                    >
                      {field?.name}
                    </text>
                    <text
                      x={field?.coordinates?.x + field?.coordinates?.width / 2}
                      y={field?.coordinates?.y + field?.coordinates?.height / 2 + 8}
                      textAnchor="middle"
                      className="fill-white text-sm font-bold pointer-events-none"
                    >
                      {getMetricValue(field)}
                    </text>
                  </g>
                ))}
              </svg>
              
              {/* Legend */}
              <div className="absolute bottom-4 left-4 bg-card border border-border rounded-lg p-3 agricultural-shadow">
                <div className="text-xs font-medium text-foreground mb-2">
                  {metricOptions?.find(m => m?.id === selectedMetric)?.label}
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 rounded-sm bg-success"></div>
                    <span className="text-xs text-muted-foreground">High</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 rounded-sm bg-warning"></div>
                    <span className="text-xs text-muted-foreground">Medium</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 rounded-sm bg-error"></div>
                    <span className="text-xs text-muted-foreground">Low</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Field Details Panel */}
          <div className="space-y-4">
            <h3 className="font-semibold text-foreground">Field Details</h3>
            
            {selectedField ? (
              <div className="bg-muted/30 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-foreground">{selectedField?.name}</h4>
                  <Button variant="ghost" size="icon" onClick={() => setSelectedField(null)}>
                    <Icon name="X" size={16} />
                  </Button>
                </div>
                
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-muted-foreground">Crop:</span>
                    <div className="font-medium text-foreground">{selectedField?.cropType}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Size:</span>
                    <div className="font-medium text-foreground">{selectedField?.acres} acres</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Effectiveness:</span>
                    <div className="font-medium text-success">{selectedField?.effectiveness}%</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Applications:</span>
                    <div className="font-medium text-foreground">{selectedField?.applications}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total Cost:</span>
                    <div className="font-medium text-foreground">${selectedField?.cost}</div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Pest Pressure:</span>
                    <div className={`font-medium ${
                      selectedField?.pestPressure === 'Low' ? 'text-success' :
                      selectedField?.pestPressure === 'Medium' ? 'text-warning' : 'text-error'
                    }`}>
                      {selectedField?.pestPressure}
                    </div>
                  </div>
                </div>
                
                <div className="pt-3 border-t border-border">
                  <span className="text-xs text-muted-foreground">Last Treatment:</span>
                  <div className="text-sm font-medium text-foreground">
                    {new Date(selectedField.lastTreatment)?.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                </div>
                
                <Button variant="outline" size="sm" fullWidth iconName="Eye">
                  View Treatment History
                </Button>
              </div>
            ) : (
              <div className="bg-muted/30 rounded-lg p-8 text-center">
                <Icon name="MousePointer" size={32} className="mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  Click on a field zone to view detailed information
                </p>
              </div>
            )}
            
            {/* Quick Stats */}
            <div className="space-y-3">
              <h4 className="font-medium text-foreground">Farm Overview</h4>
              <div className="grid grid-cols-1 gap-3">
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-lg font-bold text-foreground">114.5</div>
                  <div className="text-xs text-muted-foreground">Total Acres</div>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-lg font-bold text-success">86%</div>
                  <div className="text-xs text-muted-foreground">Avg Effectiveness</div>
                </div>
                <div className="bg-muted/30 rounded-lg p-3">
                  <div className="text-lg font-bold text-primary">$5,680</div>
                  <div className="text-xs text-muted-foreground">Total Treatment Cost</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldHeatMap;